import React, { useEffect, useState } from "react";
import loadable from "../../Layout/loadable";
import { useSelector } from "react-redux";
import ReactGA from "react-ga";
import { Helmet } from "react-helmet";
import FooterComponent from "./../../Generic/Footer/FooterComponent";
import HeaderComponent from "./../../Generic/Header/HeaderComponent";
import SeoMeta from "./../../Generic/SEO/SeoMeta";
import ServiceAreaSelect from "./ServiceArea/ServiceAreaSelect";
import FlexiAssetsBanner from "./FlexiAssetsBanner/FlexiAssetsBanner";
import * as endpoint from "./../../../Service/endpoints";
import { getPopular, getProductsByCategory, getTrending } from "../../../Service/catalog";
import { canonicalUrl } from "../../../Service/seoUrl";
import {
  BlogLinks,
  CategoryShowcase,
  FinalCTA,
  HOME_FAQ,
  HomeFAQ,
  HomeHero,
  HowItWorks,
  ProductSection,
  Reviews,
  TrustStrip,
  WhyVoorent,
} from "./HomeSections";
import "./home.css";

// Campaign-only form: loaded on demand for ?medium= visitors.
const ResponseForm = loadable(() => import(/* webpackChunkName: "campaign" */ "./GoogleForm/ResponseForm"));

const loadPremium = () => getProductsByCategory("premium");
const loadPackages = () => getProductsByCategory("package");
const loadSeasonal = () => getProductsByCategory("seasonal");

function HomeComponent({ match }) {
  const surveyModal = useSelector((state) => state.surveyModal);
  const [openResponseForm, setOpenResponseForm] = useState(false);
  const isCampaign = window.location.href.indexOf("medium") > -1;

  useEffect(() => {
    ReactGA.initialize("UA-125546749-1");
    ReactGA.pageview(window.location.pathname);
  }, []);

  // Campaign visitors (?medium=…) see the response form after 5 seconds.
  useEffect(() => {
    if (!isCampaign) return undefined;
    const timer = setTimeout(() => setOpenResponseForm(true), 5000);
    return () => clearTimeout(timer);
  }, [isCampaign]);

  const citySlug = (match && match.params && match.params.city) || "";
  const cityName = citySlug ? citySlug.charAt(0).toUpperCase() + citySlug.slice(1) : "Delhi & Gurgaon";
  const currentCity = endpoint.getCurrentCity();
  const heroCity = currentCity.charAt(0).toUpperCase() + currentCity.slice(1);
  const canonical = citySlug ? `https://voorent.com/${citySlug}` : "https://voorent.com/";
  const title = citySlug
    ? `Furniture and Appliances on Rent in ${cityName} | Voorent`
    : "Furniture on Rent | Appliances on Rent | Voorent";
  const description = citySlug
    ? `Rent furniture and appliances online in ${cityName} with flexible plans, affordable pricing, and doorstep delivery from Voorent.`
    : "Rent furniture and appliances online in Delhi and Gurgaon with flexible plans, affordable pricing, and doorstep delivery from Voorent.";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Store",
      name: "Voorent",
      url: canonicalUrl(canonical),
      image: "https://voorent.com/voorent-storage/images/banner_1.png",
      description,
      areaServed: cityName,
      priceRange: "Rs",
      email: "support@voorent.com",
      sameAs: [
        "https://www.facebook.com/Voorent.rentals",
        "https://www.linkedin.com/company/voorent",
        "https://www.instagram.com/voorent/",
      ],
      makesOffer: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Furniture on rent" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Appliances on rent" } },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Voorent",
      url: "https://voorent.com/",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://voorent.com/search/{search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: HOME_FAQ.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];

  return (
    <React.Fragment>
      <SeoMeta
        title={title}
        description={description}
        canonical={canonical}
        image="https://voorent.com/voorent-storage/images/banner_1.png"
        structuredData={structuredData}
      />
      <Helmet>
        <meta
          name="keywords"
          content={`furniture on rent, appliances on rent, rent furniture in ${cityName}, rent appliances in ${cityName}, sofa on rent, bed on rent, AC on rent, washing machine on rent, Voorent`}
        />
      </Helmet>

      {isCampaign && (
        <div>
          {openResponseForm && <ResponseForm />}
          {!surveyModal.show && <ServiceAreaSelect />}
        </div>
      )}

      <HeaderComponent />

      <main id="main-content" className="v-main v-home">
        <HomeHero cityName={heroCity} />
        <TrustStrip />
        <CategoryShowcase city={currentCity} />
        <ProductSection
          id="v-trending-title"
          eyebrow="Trending rentals"
          title="What people are renting now"
          load={getTrending}
        />
        <ProductSection
          id="v-premium-title"
          eyebrow="Premium collection"
          title="Statement pieces, on a monthly plan"
          description="Designer sofas, beds and tables from the Voorent premium range."
          load={loadPremium}
          viewAll={endpoint.getPath("premium")}
          defer
          tone="inverse"
        />
        <ProductSection
          id="v-featured-title"
          eyebrow="Featured"
          title="Most viewed"
          load={getPopular}
          limit={4}
          defer
        />
        <HowItWorks />
        <ProductSection
          id="v-packages-title"
          eyebrow="Complete packages"
          title="Move-in ready bundles"
          description="Furniture and appliance combos for homes, PGs and co-living spaces."
          load={loadPackages}
          viewAll={endpoint.getPath("package")}
          defer
        />
        <ProductSection
          id="v-seasonal-title"
          eyebrow="Seasonal essentials"
          title="Rent for the season you need"
          load={loadSeasonal}
          viewAll={endpoint.getPath("seasonal")}
          defer
        />
        <WhyVoorent />
        <div className="v-container">
          <FlexiAssetsBanner />
        </div>
        <Reviews />
        <HomeFAQ />
        <BlogLinks />
        <FinalCTA />
      </main>

      <FooterComponent />
    </React.Fragment>
  );
}

export default HomeComponent;
